<script setup lang="ts">
import PlusMenuLink from '@layout/nav-v2/mobile/components/PlusMenuLink.vue';
import {
  URL_BUYER_INVOICES, URL_OFFER_CREATE,
  URL_USER_DEALS,
  URL_USER_NOTIFICATIONS, URL_USER_ORGANIZATION_MANAGE,
  URL_USER_ORGANIZATION_PAYMENT_METHOD_BUYER, URL_USER_ORGANIZATION_TEAM,
  URL_USER_PROFILE_MANAGE,
  URL_USER_PROPOSAL,
} from '@tools/routes/routes';
import type { Submenu } from '@global-types/nav-v2/menuItems';
import IconHeartStraight from '@icons/ph_heart-straight.svg';
import IconAlignTop from '@icons/ph_align-top.svg';
import IconRent from '@icons/ph_rent.svg';
import { trans } from '@elements/translation.ts';
import Icon from '@components/Icon.vue';
import { storeToRefs } from 'pinia';
import { useNavbarStore } from '@stores/layout/useNavbarStore';
import Divider from '@components/Divider.vue';
import IconUserCircle from '@icons/ph_user-circle.svg';
import IconBellRinging from '@icons/ph_bell-ringing.svg';
import IconPlus from '@icons/ph_plus.svg';
import PlusMenuItemGroup from '@layout/nav-v2/mobile/components/PlusMenuItemGroup.vue';
import IconBriefCase from '@icons/ph_briefcase.svg';
import MenuAccountManagerContact from '@layout/nav-v2/MenuAccountManagerContact.vue';
import { useUserProfileStore } from '@stores/useUserProfileStore.ts';
import { MenuItemGroup as MenuItemGroupEnum } from '@enums/menuItemGroups.ts';
import { computed } from 'vue';

const navbarStore = useNavbarStore();
const { notifications } = storeToRefs(navbarStore);
const { isCurrentDropdownOpen, setCurrentDropdownOpen } = navbarStore;

const { user, hasPayBuyer } = storeToRefs(useUserProfileStore());

const subMenuOrganization = computed<Submenu[]>(() => {
  const items = [
    { name: trans('user_menu.organization'), link: URL_USER_ORGANIZATION_MANAGE },
    { name: trans('user_menu.team'), link: URL_USER_ORGANIZATION_TEAM },
  ];

  if (hasPayBuyer.value) {
    items.push(
      { name: trans('user_menu.gestion_parameters'), link: URL_USER_ORGANIZATION_PAYMENT_METHOD_BUYER },
    );
  }

  return items;
});
</script>

<template>
  <li>
    <PlusMenuLink
      :link="URL_USER_PROPOSAL"
      :has-pin="notifications.unread_match_proposal > 0"
    >
      <template #icon="{ isActive }">
        <Icon
          :svg="IconHeartStraight"
          width="24"
          height="24"
          :fill="isActive ? 'black' : 'gray-darker'"
        />
      </template>

      <template #text>
        {{ trans('user_menu.selection_item') }}

        <template v-if="notifications.unread_match_proposal > 0">
          ({{ notifications.unread_match_proposal }})
        </template>
      </template>
    </PlusMenuLink>
  </li>

  <li>
    <PlusMenuLink :link="URL_USER_DEALS">
      <template #icon="{ isActive }">
        <Icon
          :svg="IconAlignTop"
          width="24"
          height="24"
          :fill="isActive ? 'black' : 'gray-darker'"
        />
      </template>

      <template #text>
        {{ trans('user_menu.deals') }}
      </template>
    </PlusMenuLink>
  </li>

  <li class="px-3">
    <Divider class="m-0" color="gray" />
  </li>

  <li>
    <PlusMenuItemGroup
      :is-dropdown-open="isCurrentDropdownOpen(MenuItemGroupEnum.MY_ORGANIZATION)"
      :submenus="subMenuOrganization"
      @toggle-dropdown="(isOpen) => setCurrentDropdownOpen(isOpen ? MenuItemGroupEnum.MY_ORGANIZATION : null)"
    >
      <template #icon="{ isActive }">
        <Icon
          :svg="IconBriefCase"
          width="24"
          height="24"
          :fill="isActive ? 'black' : 'gray-darker'"
        />
      </template>

      <template #text>
        {{ trans('user.menu.my_organization') }}
      </template>
    </PlusMenuItemGroup>
  </li>

  <li>
    <PlusMenuLink :link="URL_USER_PROFILE_MANAGE">
      <template #icon="{ isActive }">
        <Icon
          :svg="IconUserCircle"
          width="24"
          height="24"
          :stroke="isActive ? 'black' : 'gray-darker'"
        />
      </template>

      <template #text>
        {{ trans('user_menu.profile') }}
      </template>
    </PlusMenuLink>
  </li>

  <li>
    <PlusMenuLink :link="URL_USER_NOTIFICATIONS">
      <template #icon="{ isActive }">
        <Icon
          :svg="IconBellRinging"
          width="24"
          height="24"
          :fill="isActive ? 'black' : 'gray-darker'"
        />
      </template>

      <template #text>
        {{ trans('user_menu.notifications') }}
      </template>
    </PlusMenuLink>
  </li>

  <li class="px-3">
    <Divider class="m-0" color="gray" />
  </li>

  <li>
    <PlusMenuLink :link="URL_BUYER_INVOICES">
      <template #icon="{ isActive }">
        <Icon
          :svg="IconRent"
          width="24"
          height="24"
          :stroke="isActive ? 'black' : 'gray-darker'"
        />
      </template>

      <template #text>
        {{ trans('user_menu.invoices') }}
      </template>
    </PlusMenuLink>
  </li>

  <li class="px-3">
    <Divider class="m-0" color="gray" />
  </li>

  <li>
    <PlusMenuLink
      :link="URL_OFFER_CREATE"
    >
      <template #icon="{ isActive }">
        <Icon
          :svg="IconPlus"
          width="24"
          height="24"
          :fill="isActive ? 'black' : 'gray-darker'"
        />
      </template>

      <template #text>
        {{ trans('user_menu.main.create_offer') }}
      </template>
    </PlusMenuLink>
  </li>

  <li class="px-3">
    <Divider class="m-0" color="gray" />
  </li>
</template>
